import { Box, Button, Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import {
    CalculateProfit,
    generatePlChartData,
    getCurentOrderPricesFromData
} from '../../util/tradepanel'
import EmptyBox from '../../assets/icons/EmptyBox'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SimpleBar from 'simplebar'
import { setOrderDataFunction, setSelectedStrategy } from '../../reducer/orders.reducer'
import { useDispatch, useSelector } from 'react-redux'
import commonActions from '../../actions/common.actions'
import { CalculateMarginResponse, TransactionType, Broker_Variety } from '../../proto/generated/order'
import { getMargin } from '../../services/apis/misc'
import PlChart, { OiDataTypeBars } from '../../pages/home/plChart'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { selectSnapshot } from '../../selectors/security'
import { WidgetListItems } from '../../util/sidebar'
import Trade from '../../assets/icons/Trade'
import { selectBasketId, selectStrategyBuilderMode, selectStrategyBuilderTab, selectTradeOrders } from '../../selectors/strategyBuilder'
import { OptionChainViewKey, ViewType } from '../../assets/constants'
import { StrategyBuilderTab } from '../strategyBuilder/header'
import useSelectView from '../../hooks/useSelectView'
import ReadOnlyBasketWisePositionContainer from '../postionsWidget/ReadOnlyBasketPositionContainer'
import { selectBasketAnalyzeDetails, selectOrdersData, selectSelectedStrategy } from '../../selectors/orders'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { matchPath, useLocation } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import { setSimulatorOrderDataFunction } from '../../reducer/simulator.reducer'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import TradeContainer from '../../pages/simulator/tradeContainer'
import TimeControls from '../../pages/simulator/timeControls'
import PositionsAndOrdersWrapper from '../../pages/simulator/PositionsAndOrdersWrapper'
import { selectSimulatorDate, selectSimulatorSecurityKey } from '../../selectors/simulator'
import UndoRedoButtons from '../UndoRedoFunctonalities/UndoRedoButtons'
import { ActionType, useUndoRedoContext } from '../UndoRedoFunctonalities/UndoRedo.context'
import { useUser } from '../../context/UserContext'
import TradePanel from './tradePanel'
import { resetStrategyBuilder } from '../../reducer/strategyBuilder.reducer'
import AnalyzePositionsTable from '../analyzePositionsTable/analyzePositionsTable'
import useSetPrimarySecurity from '../../hooks/useSetPrimarySecurity'
import { selectPositions } from '../../selectors/positions'
import { selectPrimaryTradingAccount, selectUserDetails } from '../../selectors/user'
import BinIcon from '../../assets/icons/BinIcon.tsx'

const TradePanelContainer = () => {
    const dispatch = useDispatch()
    const { manager, push } = useUndoRedoContext()
    const orderData = useSelector(selectOrdersData)
    const tradeOrders = useSelector(selectTradeOrders);
    const basketId = useSelector(selectBasketId);
    const setPrimarySecurity = useSetPrimarySecurity();
    const {
        orderState,
        orderSecurityKey: tradePanelSecurityKey, product, orderType
    } = orderData
    const { orders, ordersPrices, allOrders } = useCombineBasketAnalyzeAndLegOrders()
    const strategyBuilderMode = useSelector(selectStrategyBuilderMode);
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const simulatorSecurityKey = useSelector(selectSimulatorSecurityKey)
    const scrollRef = useRef(null)
    const positions = useSelector(selectPositions);
    const selectedStrategy = useSelector(selectSelectedStrategy)
    const strategyName = selectedStrategy?.strategyName
    const [profitData, setProfitData] = useState<OiDataTypeBars[]>([])
    const { primaryOrUnderlying: mainSecurity, primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity()
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const mainSecuritySnapshot = useSelector(selectSnapshot(underlyingToken))
    const profitsDataCreated = useRef<boolean>(false)
    const strategySelectedTab = useSelector(selectStrategyBuilderTab)
    const view = useSelectView()
    const location = useLocation()
    const basketAnalyzeDetails = useSelector(selectBasketAnalyzeDetails)
    const showPlChart = useMemo(() => (view === ViewType.STRATEGY_BUILDER && strategySelectedTab !== StrategyBuilderTab.Payoff) || (view === ViewType.SIMULATOR && strategySelectedTab !== StrategyBuilderTab.Payoff), [strategySelectedTab, view])
    const viewKey = useMemo(() => {
        if (matchPath(ROUTES.simulator, location.pathname)) {
            return OptionChainViewKey.HISTORICAL
        }
        return OptionChainViewKey.LIVE

    }, [location.pathname])
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const { user: { simulatorAccessible } } = useUser()


    const [tradePanelSecurity, setTradePanelSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const basketPositionKey = useMemo(() => {
        const basketPositions = positions.filter(p => p.basketId === basketId);
        return basketPositions.length ? getSecurityKey(basketPositions[0].security.exchange, basketPositions[0].security.code) : ''
    }, [positions, basketId])
    useEffect(() => {
        if (!basketPositionKey) { setTradePanelSecurity(undefined); return }
        getSecurityAsync(basketPositionKey).then((sec) => {
            if (sec?.underlyingSecurityCode) getSecurityAsync(getSecurityKey(sec.exchange, sec.underlyingSecurityCode)).then(setTradePanelSecurity)
            else setTradePanelSecurity(sec)
        })
    }, [basketPositionKey])


    useEffect(() => {
        if (scrollRef.current) {
            new SimpleBar(scrollRef.current)
        }
        if (['square-off', 'waiting-square-off', 'complete'].includes(orderState)) {
            setOrderDataFunction({ orders: [] })
        }
    }, [])

    // useEffect(() => {
    //     if (!underlyingToken.length) {
    //         commonActions.dispatchRightSideWidget(dispatch, null)
    //     }
    // }, [underlyingToken])

    const previousUnderlyingToken = useRef("");
    useEffect(() => {
        if (!underlyingToken || !previousUnderlyingToken.current) return;

        if (previousUnderlyingToken.current !== underlyingToken) {
            profitsDataCreated.current = false
            if (viewKey === OptionChainViewKey.LIVE) {
                dispatch(resetStrategyBuilder())
            } else {
                manager.clear({})
                push({ previousTimeStamp: simulatorTimestamp, timeStamp: simulatorTimestamp }, 'simultaor time change', ActionType.SIMULATOR_TIME_CHANGE)
                setSimulatorOrderDataFunction({ orders: [], resetOrderState: true })
            }
        }
    }, [underlyingToken])

    useEffect(() => {
        if (orders && orders.length > 0 && profitData && profitData.length > 0) {
            // const ordersPrices = calculateLimitValue()
            mainSecurity && CalculateProfit(profitData, orders, ordersPrices, mainSecurity, setProfitData, 0, viewKey === OptionChainViewKey.LIVE ? Date.now() : simulatorTimestamp)
        }
    }, [orders, mainSecurity, ordersPrices, viewKey, simulatorTimestamp])

    const handleClearTrade = useCallback((e: any) => {
        e.stopPropagation()
        if (viewKey === OptionChainViewKey.LIVE) {
            setOrderDataFunction({ orders: [], resetOrderState: true })
            commonActions.dispatchRightSideWidget(dispatch, null)
        } else {
            manager.clear({})
            push({ previousTimeStamp: simulatorTimestamp, timeStamp: simulatorTimestamp }, 'simultaor time change', ActionType.SIMULATOR_TIME_CHANGE)
            setSimulatorOrderDataFunction({ orders: [], resetOrderState: true })
        }
    }, [viewKey, dispatch, manager, push, simulatorTimestamp])

    const calculateMargin = useCallback((passedOrdersPrices = ordersPrices) => {
        // fetching margin
        if(!primaryTradingAccount){
            commonActions.setApiError(dispatch, {
                error: true,
                message: "Connect a trading account to place trades",
                errorType: 'info'
            })
            return
        }
        const data: any = orders.map((order, i) => ({
            exchange: order.security?.exchange,
            position: order.quantity * (order.security?.lotSize ?? 0),
            price: Number((Number(passedOrdersPrices[i]?.price) * (mainSecurity?.priceMultiplier ?? 100)).toFixed(0)),
            security: {
                exchange: order.security.exchange,
                code: order.security.code
            },
            transactionType: order.type === 'buy' ? TransactionType.BUY : TransactionType.SELL,
            strike_price: order.security.strikePrice,
            expiry: order.security.expiry.expiry,
            optionType: order.security.optionType,
            orderType: orderType,
            product: product,
            variety: Broker_Variety.REGULAR
        }))
        if (selectedStrategy) {
            dispatch(setSelectedStrategy({
                ...selectedStrategy,
                strategyName: undefined
            }))
        }
        getMargin({
            body: {
                orders: data,
                tradingAccount: {
                    accountId : primaryTradingAccount.accountId,
                    brokerId : primaryTradingAccount.brokerCode
                }
            }
        }).then((res: CalculateMarginResponse) => {
            if (selectedStrategy) {
                dispatch(setSelectedStrategy({
                    ...selectedStrategy,
                    strategyName: res.strategyName
                }))
            }
        }).catch(e => {
            console.error("Error fetching margin")
        })
    }, [ordersPrices, orders, mainSecurity, product, orderType])

    useEffect(() => {
        (async () => {
            if (!profitsDataCreated.current) {
                const newOptionsExpiries = [...(optionExpiries ?? [])]
                let index = Infinity
                if (orders.length > 0) {
                    index = newOptionsExpiries.findIndex(d => d.expiration.expiry?.expiry === orders[0].security?.expiry?.expiry)
                    orders.forEach((order, i) => {
                        if (i > 0) {
                            const newIndex = newOptionsExpiries.findIndex(d => d.expiration.expiry?.expiry === orders[i].security?.expiry?.expiry)
                            if (newIndex !== -1 && newIndex < index) {
                                index = newIndex
                            }
                        }
                    })
                }
                if (index === -1 || !isFinite(index)) {
                    index = 0
                }
                const data = newOptionsExpiries.filter(t => !t.derived)?.[index]?.trades
                if (data && data.length > 0) {
                    const createdProfitData = generatePlChartData({ data, ltp: mainSecuritySnapshot?.lastTradedPrice, priceMultiplier: mainSecurity?.priceMultiplier })
                    setProfitData(createdProfitData)
                    if (createdProfitData && createdProfitData?.length > 0) {
                        profitsDataCreated.current = true
                        if (orders && orders.length > 0 && createdProfitData && createdProfitData.length > 0) {
                            const newOrdersPrices = await getCurentOrderPricesFromData(orders, optionExpiries ?? [], underlyingToken, [])
                            // const createdOrdersPrices = calculateLimitValue(newOrdersPrices)
                            mainSecurity && CalculateProfit(createdProfitData, orders, newOrdersPrices, mainSecurity, setProfitData, 0, viewKey === OptionChainViewKey.LIVE ? Date.now() : simulatorTimestamp)
                        }
                    }
                }
            }
        })()
    }, [optionExpiries, orders])

    const goToPositionsHandler = useCallback(() => {
        commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.POSITIONS)
    }, [dispatch])

    const emptyBoxContainer = useMemo(() => {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <EmptyBox />
                    <Typography style={{
                        fontSize: '18px',
                        textAlign: 'center',
                        maxWidth: '320px',
                        marginTop: '20px'
                    }}>
                        No security added to trade.
                    </Typography>
                </Box>
            </Box>
        )
    }, [])

    const topBarContainer = useMemo(() => {
        return (
            <Grid container px={'8px'} py={'2.5px'} justifyContent={'space-between'} alignItems={'center'}>
                <Stack direction={'row'} gap={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Trade sx={{ color: (theme) => theme.palette.text.secondary }} fontSize={"large"} />
                        <Typography variant={'body1'}>{viewKey === OptionChainViewKey.LIVE ? 'Trade' : 'Simulated Trade'}</Typography>
                    </Box>
                    {
                        viewKey === OptionChainViewKey.LIVE ?
                            null :
                            <UndoRedoButtons />
                    }
                </Stack>
                {
                    (allOrders.length && viewKey === OptionChainViewKey.HISTORICAL) ?
                        <Button data-event={'Clear Trade Clicked'} data-event-source={'Trade'} variant="outlined" color={'inherit'} onClick={handleClearTrade}
                            startIcon={<BinIcon />} size={'small'}>
                            {
                                "New Simulation"
                            }
                        </Button> :
                        null
                }
            </Grid>
        )
    }, [allOrders, viewKey, handleClearTrade, strategyBuilderMode, basketId])

    const plChart = useMemo(() => {
        return showPlChart ? (
            <div className="chart-container-wrap"
                style={{ height: '180px', width: '100%' }}>
                <PlChart readOnly={true} optionChainViewKey={viewKey} />
            </div>
        ) : null
    }, [showPlChart, viewKey])

    const readOnlyBaseketContainer = useMemo(() => {
        return basketAnalyzeDetails ? (
            <div style={{ width: '100%' }}>
                <ReadOnlyBasketWisePositionContainer />
            </div>
        ) : null
    }, [basketAnalyzeDetails])

    const divider = useMemo(() => <Divider style={{ width: '100%' }} />, [])

    const strategyDisplayName = useMemo(() => {
        return (`${mainSecurity?.symbol ?? ''} - ${strategyName ?? ''}`)
    }, [mainSecurity?.symbol, strategyName])

    const showSimulatorTimeControls = useMemo(() => {
        return allOrders.some((order) => !order.traded)
    }, [allOrders])

    const body = useMemo(() => (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',

            }}
        >
            {
                viewKey === OptionChainViewKey.HISTORICAL ? <>
                    {showSimulatorTimeControls ? plChart : null}
                    <Box sx={{
                        flexGrow: 1, overflowY: 'auto',
                        minHeight: 0,
                    }}>
                        <TradeContainer />
                        <PositionsAndOrdersWrapper />
                    </Box>
                    {showSimulatorTimeControls ? (
                        <>
                            <Divider sx={{ m: 0 }} />
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    minHeight: '128px',
                                    overflowY: 'auto',
                                    padding: 2
                                }}
                            >
                                <TimeControls />
                            </Box>
                        </>
                    ) : null}
                </> :
                    strategyBuilderMode === 'trade' ? ( tradeOrders.length ? <TradePanel /> : emptyBoxContainer) :
                        <>
                            <AnalyzePositionsTable positions={positions} />
                            {/* {plChart}
                            {readOnlyBaseketContainer} */}
                            <TradePanel
                                // calculateMargin={calculateMargin}
                                // goToPositionsHandler={goToPositionsHandler}
                                // profitData={profitData}
                            />
                        </>
            }
        </Box>), [viewKey, tradeOrders.length, positions, basketAnalyzeDetails, plChart, readOnlyBaseketContainer, strategyDisplayName, calculateMargin, goToPositionsHandler, profitData, emptyBoxContainer, showSimulatorTimeControls, strategyBuilderMode])

    const jsxTree = useMemo(() => {
        return (<Paper
            ref={scrollRef}
            sx={{
                display: 'flex',
                height: '100%',
                flexDirection: 'column',
                borderTopRightRadius: '6px',
                overflow: 'auto',
                position: 'relative',
                filter: (viewKey === OptionChainViewKey.HISTORICAL && !simulatorAccessible) ? 'blur(4px)' : 'none',
                pointerEvents: (viewKey === OptionChainViewKey.HISTORICAL && !simulatorAccessible) ? 'none' : 'all',
            }}
        >
            <Grid container>
                {topBarContainer}
                {divider}
            </Grid>
            {body}
        </Paper>)
    }, [topBarContainer, divider, body])

    return (jsxTree)
}

export default memo(TradePanelContainer)